import React, { useState, useEffect } from 'react';
import { GraduationCap, User, Calendar, MapPin, Users, Search, Plus, X } from 'lucide-react';
import FormInput from '../../FormInput';
import { toast } from 'react-toastify';
import api from '../../api';

const SoutenanceForm = ({
  formData,
  formErrors,
  selectedSoutenance,
  onInputChange,
  onSubmit,
  submitting,
  typesSoutenance,
  jurys,
  salles,
  enseignantsInternes,
  enseignantsExternes
}) => {
  const [classes, setClasses] = useState([]);
  const [etudiants, setEtudiants] = useState([]);
  const [selectedClasse, setSelectedClasse] = useState('');
  const [selectedEtudiants, setSelectedEtudiants] = useState([]);
  const [etudiantRoles, setEtudiantRoles] = useState({});
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingEtudiants, setLoadingEtudiants] = useState(false);
  const [searchEtudiant, setSearchEtudiant] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [originalEtudiants, setOriginalEtudiants] = useState([]);

  // Charger les classes au montage du composant
  useEffect(() => {
    loadClasses();
  }, []);

  // Réinitialiser le mode édition quand selectedSoutenance change
  useEffect(() => {
    if (!selectedSoutenance) {
      setIsEditMode(false);
      setSelectedClasse('');
      setSelectedEtudiants([]);
      setEtudiantRoles({});
    }
  }, [selectedSoutenance]);

  // Initialiser les données lors de la modification d'une soutenance existante
  useEffect(() => {
    if (selectedSoutenance && selectedSoutenance.etudiants_soutenance && selectedSoutenance.etudiants_soutenance.length > 0) {
      setIsEditMode(true);
      
      // Extraire les IDs des étudiants et leurs rôles
      const etudiantsData = selectedSoutenance.etudiants_soutenance
        .filter(es => es && es.etudiant)
        .map(es => es.etudiant);
      
      // Stocker les étudiants originaux pour référence
      setOriginalEtudiants(etudiantsData);
      
      if (etudiantsData.length > 0) {
        // Pré-sélectionner les étudiants
        const etudiantIds = etudiantsData.map(es => es.id);
        setSelectedEtudiants(etudiantIds);
        
        // Pré-remplir les rôles des étudiants
        const roles = {};
        selectedSoutenance.etudiants_soutenance.forEach((es, index) => {
          if (es.etudiant) {
            roles[es.etudiant.id] = es.role || (index === 0 ? 'PRINCIPAL' : 'CO_AUTEUR');
          }
        });
        setEtudiantRoles(roles);
        
        // Récupérer la classe du premier étudiant
        const premierEtudiant = etudiantsData[0];
        const determinerClasse = async () => {
          try {
            const response = await api.get(`/soutenance/etudiants/${premierEtudiant.id}/classe/`);
            if (response.data.status === 'success') {
              const classeData = response.data.data;
              setSelectedClasse(classeData.id);
              await loadEtudiants(classeData.id);
            }
          } catch (error) {
            console.error('Erreur lors de la récupération de la classe:', error);
            // Si on ne peut pas récupérer la classe, on essaie de la trouver dans les classes disponibles
            if (classes.length > 0) {
              for (const classe of classes) {
                try {
                  const response = await api.get(`/soutenance/classes/${classe.id}/etudiants/`);
                  if (response.data.status === 'success') {
                    const etudiantsClasse = response.data.data?.etudiants || [];
                    const etudiantTrouve = etudiantsClasse.find(e => e.id === premierEtudiant.id);
                    if (etudiantTrouve) {
                      setSelectedClasse(classe.id);
                      await loadEtudiants(classe.id);
                      break;
                    }
                  }
                } catch (error) {
                  console.error(`Erreur lors de la vérification de la classe ${classe.id}:`, error);
                }
              }
            }
          }
        };
        
        determinerClasse();
      }
    } else {
      setIsEditMode(false);
      setOriginalEtudiants([]);
    }
  }, [selectedSoutenance]);

  // Charger les étudiants quand une classe est sélectionnée
  useEffect(() => {
    if (selectedClasse) {
      loadEtudiants(selectedClasse);
    } else {
      setEtudiants([]);
      // Ne pas réinitialiser les étudiants sélectionnés si on est en mode modification
      if (!isEditMode) {
      setSelectedEtudiants([]);
      setEtudiantRoles({});
    }
    }
  }, [selectedClasse, isEditMode]);

  // Synchroniser les étudiants sélectionnés avec formData
  useEffect(() => {
    // S'assurer qu'il y a des étudiants sélectionnés
    if (selectedEtudiants.length === 0) {
      onInputChange({ 
        target: { 
          name: 'etudiants', 
          value: [] 
        } 
      });
      return;
    }
    
    // Préparer les données des étudiants au format attendu par l'API
    const etudiantsData = selectedEtudiants.map((etudiantId, index) => {
      return {
        etudiant_id: etudiantId,
        role: etudiantRoles[etudiantId] || (index === 0 ? 'PRINCIPAL' : 'CO_AUTEUR'),
        ordre_presentation: index + 1
      };
    });

    console.log('Données étudiants formatées pour API:', etudiantsData);

    onInputChange({ 
      target: { 
        name: 'etudiants', 
        value: etudiantsData
      } 
    });
  }, [selectedEtudiants, etudiantRoles]);

  const loadClasses = async () => {
    setLoadingClasses(true);
    try {
      const response = await api.get('/soutenance/classes-pour-soutenance/');
      
      if (response.data.status === 'success') {
        const classesData = response.data.data || [];
        setClasses(classesData);
        if (classesData.length === 0) {
          toast.info('Aucune classe avec des étudiants inscrits trouvée');
        }
      } else {
        toast.error(`Erreur: ${response.data.message || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('Erreur de connexion:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur de connexion au serveur';
      toast.error(errorMessage);
    } finally {
      setLoadingClasses(false);
    }
  };

  const loadEtudiants = async (classeId) => {
    setLoadingEtudiants(true);
    try {
      const response = await api.get(`/soutenance/classes/${classeId}/etudiants/`, {
        params: { search: searchEtudiant }
      });

      if (response.data.status === 'success') {
        const etudiantsData = response.data.data?.etudiants || [];
        setEtudiants(etudiantsData);
        
        // En mode édition, filtrer les étudiants sélectionnés pour ne garder que ceux de la nouvelle classe
        if (isEditMode && selectedEtudiants.length > 0) {
          const etudiantsDisponibles = etudiantsData.map(e => e.id);
          const etudiantsValides = selectedEtudiants.filter(id => etudiantsDisponibles.includes(id));
          
          if (etudiantsValides.length !== selectedEtudiants.length) {
            // Certains étudiants ne sont plus dans cette classe
            setSelectedEtudiants(etudiantsValides);
            
            // Nettoyer les rôles des étudiants supprimés
            const nouveauxRoles = {};
            etudiantsValides.forEach(id => {
              if (etudiantRoles[id]) {
                nouveauxRoles[id] = etudiantRoles[id];
              }
            });
            setEtudiantRoles(nouveauxRoles);
            
            if (etudiantsValides.length === 0) {
              toast.warning('Aucun des étudiants sélectionnés n\'est dans cette classe. Veuillez sélectionner de nouveaux étudiants.');
            } else {
              toast.info(`${selectedEtudiants.length - etudiantsValides.length} étudiant(s) supprimé(s) car ils ne sont pas dans cette classe.`);
            }
          }
        }
      } else {
        toast.error(`Erreur: ${response.data.message || 'Erreur lors du chargement des étudiants'}`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      const errorMessage = error.response?.data?.message || 'Erreur de connexion';
      toast.error(errorMessage);
    } finally {
      setLoadingEtudiants(false);
    }
  };

  const handleClasseChange = (e) => {
    const classeId = e.target.value;
    setSelectedClasse(classeId);
    
    // En mode édition, ne pas réinitialiser les étudiants sélectionnés
    // pour permettre leur modification, mais permettre de changer de classe
    if (!isEditMode) {
    setSelectedEtudiants([]);
    setEtudiantRoles({});
    } else {
      // En mode édition, si on change de classe, on peut garder les étudiants
      // mais on doit vérifier s'ils sont dans la nouvelle classe
      // On va les filtrer après le chargement des étudiants
    }
    setSearchEtudiant('');
  };

  const handleEtudiantToggle = (etudiantId) => {
    setSelectedEtudiants(prev => {
      if (prev.includes(etudiantId)) {
        // Supprimer l'étudiant et son rôle
        const newEtudiantRoles = {...etudiantRoles};
        delete newEtudiantRoles[etudiantId];
        setEtudiantRoles(newEtudiantRoles);
        
        return prev.filter(id => id !== etudiantId);
      } else {
        // Ajouter l'étudiant avec un rôle par défaut
        const newRole = prev.length === 0 ? 'PRINCIPAL' : 'CO_AUTEUR';
        setEtudiantRoles(roles => ({
          ...roles,
          [etudiantId]: newRole
        }));
        
        return [...prev, etudiantId];
      }
    });
  };

  const handleEtudiantRoleChange = (etudiantId, role) => {
    setEtudiantRoles(prev => ({
      ...prev,
      [etudiantId]: role
    }));
  };

  const handleSearchEtudiant = (e) => {
    setSearchEtudiant(e.target.value);
  };

  const handleSelectAllEtudiants = () => {
    const etudiantsDisponibles = etudiants.filter(etudiant => !etudiant.a_soutenance);
    const allIds = etudiantsDisponibles.map(etudiant => etudiant.id);
    
    if (selectedEtudiants.length === allIds.length) {
      setSelectedEtudiants([]);
      setEtudiantRoles({});
    } else {
      // Définir les rôles par défaut pour tous les étudiants
      const newRoles = {};
      allIds.forEach((id, index) => {
        newRoles[id] = index === 0 ? 'PRINCIPAL' : 'CO_AUTEUR';
      });
      
      setSelectedEtudiants(allIds);
      setEtudiantRoles(newRoles);
    }
  };

  const moveEtudiantUp = (index) => {
    if (index > 0) {
      const newSelectedEtudiants = [...selectedEtudiants];
      const temp = newSelectedEtudiants[index];
      newSelectedEtudiants[index] = newSelectedEtudiants[index - 1];
      newSelectedEtudiants[index - 1] = temp;
      setSelectedEtudiants(newSelectedEtudiants);
    }
  };

  const moveEtudiantDown = (index) => {
    if (index < selectedEtudiants.length - 1) {
      const newSelectedEtudiants = [...selectedEtudiants];
      const temp = newSelectedEtudiants[index];
      newSelectedEtudiants[index] = newSelectedEtudiants[index + 1];
      newSelectedEtudiants[index + 1] = temp;
      setSelectedEtudiants(newSelectedEtudiants);
    }
  };

  return (
    <form id="soutenance-form" onSubmit={onSubmit} className="space-y-6">
      {/* Informations générales */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center space-x-2">
          <GraduationCap className="h-5 w-5" />
          <span>Informations générales</span>
        </h4>
        
        <div className="grid grid-cols-1 gap-4">
          {/* Sélection de classe */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Classe *
            </label>
            <select
              value={selectedClasse}
              onChange={handleClasseChange}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
              disabled={loadingClasses}
            >
              <option value="">
                {loadingClasses ? 'Chargement des classes...' : 'Sélectionner une classe'}
              </option>
              {classes.map(classe => (
                <option key={classe.id} value={classe.id}>
                  {classe.libelle} ({classe.effectif} étudiants)
                </option>
              ))}
            </select>
          </div>

          {/* Sélection des étudiants */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Étudiants * ({selectedEtudiants.length} sélectionné{selectedEtudiants.length > 1 ? 's' : ''})
              </label>
              {etudiants.length > 0 && (
                <button
                  type="button"
                  onClick={handleSelectAllEtudiants}
                  className="text-sm text-purple-600 hover:text-purple-800 transition-colors"
                  disabled={loadingEtudiants}
                >
                  {selectedEtudiants.length === etudiants.filter(e => !e.a_soutenance).length 
                    ? 'Désélectionner tout' 
                    : 'Sélectionner tout'
                  }
                </button>
              )}
            </div>
            
            <div className={`border rounded-lg p-3 max-h-60 overflow-y-auto ${
              formErrors.etudiants ? 'border-red-300' : 'border-gray-300'
            }`}>
              {!selectedClasse ? (
                <p className="text-gray-500 text-sm">Sélectionnez d'abord une classe</p>
              ) : loadingEtudiants ? (
                <p className="text-gray-500 text-sm">Chargement des étudiants...</p>
              ) : etudiants.length === 0 ? (
                <p className="text-gray-500 text-sm">Aucun étudiant trouvé dans cette classe</p>
              ) : (
                <div className="space-y-2">
                  {etudiants.map(etudiant => (
                    <label
                      key={etudiant.id}
                      className={`flex items-center space-x-3 p-2 rounded hover:bg-gray-50 cursor-pointer ${
                        etudiant.a_soutenance ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedEtudiants.includes(etudiant.id)}
                        onChange={() => handleEtudiantToggle(etudiant.id)}
                        disabled={etudiant.a_soutenance}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {etudiant.nom} {etudiant.prenom}
                            </div>
                            <div className="text-xs text-gray-500">
                              {etudiant.matricule}
                              {etudiant.a_soutenance && (
                                <span className="ml-2 text-orange-600 font-medium">
                                  (A déjà une soutenance)
                                </span>
                              )}
                            </div>
                          </div>
                          {selectedEtudiants.includes(etudiant.id) && (
                            <select
                              className="text-xs border border-gray-300 rounded px-2 py-1 ml-2"
                              value={etudiantRoles[etudiant.id] || (selectedEtudiants.indexOf(etudiant.id) === 0 ? 'PRINCIPAL' : 'CO_AUTEUR')}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleEtudiantRoleChange(etudiant.id, e.target.value);
                              }}
                            >
                              <option value="PRINCIPAL">Principal</option>
                              <option value="CO_AUTEUR">Co-auteur</option>
                              <option value="MEMBRE_EQUIPE">Membre équipe</option>
                            </select>
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            {formErrors.etudiants && (
              <p className="text-red-600 text-xs mt-1">{formErrors.etudiants}</p>
            )}
          </div>

          {/* Résumé des étudiants sélectionnés */}
          {selectedEtudiants.length > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <h5 className="text-sm font-medium text-purple-900 mb-2 flex items-center">
                <Users className="h-4 w-4 mr-1" />
                Étudiants sélectionnés ({selectedEtudiants.length})
              </h5>
              <div className="space-y-1">
                {selectedEtudiants.map((etudiantId, index) => {
                  // Chercher l'étudiant dans la liste actuelle ou dans les données de la soutenance
                  let etudiant = etudiants.find(e => e.id === etudiantId);
                  
                  // Si l'étudiant n'est pas dans la liste actuelle, le chercher dans les données de la soutenance
                  if (!etudiant && selectedSoutenance && selectedSoutenance.etudiants_soutenance) {
                    const etudiantSoutenance = selectedSoutenance.etudiants_soutenance.find(es => es.etudiant && es.etudiant.id === etudiantId);
                    if (etudiantSoutenance) {
                      etudiant = etudiantSoutenance.etudiant;
                    }
                  }
                  
                  const role = etudiantRoles[etudiantId] || (index === 0 ? 'PRINCIPAL' : 'CO_AUTEUR');
                  const roleDisplay = {
                    'PRINCIPAL': 'Principal',
                    'CO_AUTEUR': 'Co-auteur',
                    'MEMBRE_EQUIPE': 'Membre équipe',
                    'RAPPORTEUR': 'Rapporteur'
                  }[role] || role;
                  
                  return etudiant ? (
                    <div key={etudiantId} className="text-sm text-purple-800 flex items-center justify-between">
                      <div>
                      <span className="font-medium">
                        {index + 1}. {etudiant.nom} {etudiant.prenom}
                      </span>
                      <span className="text-purple-600 ml-2">
                        ({roleDisplay})
                      </span>
                        {/* Afficher un avertissement si l'étudiant n'est pas dans la classe actuelle */}
                        {!etudiants.find(e => e.id === etudiantId) && (
                          <span className="text-orange-600 ml-2 text-xs">
                            (Pas dans cette classe)
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleEtudiantToggle(etudiantId)}
                        className="text-red-500 hover:text-red-700 text-xs ml-2"
                        title="Supprimer cet étudiant"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <div key={etudiantId} className="text-sm text-red-600 flex items-center justify-between">
                      <span>{index + 1}. Étudiant ID: {etudiantId} (Non trouvé)</span>
                      <button
                        type="button"
                        onClick={() => handleEtudiantToggle(etudiantId)}
                        className="text-red-500 hover:text-red-700 text-xs ml-2"
                        title="Supprimer cet étudiant"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Type de soutenance et titre */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type de soutenance *
              </label>
              <select
                name="type_soutenance"
                value={formData.type_soutenance || ''}
                onChange={onInputChange}
                required
                className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${
                  formErrors.type_soutenance ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Sélectionner un type</option>
                {typesSoutenance.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.nom}
                  </option>
                ))}
              </select>
              {formErrors.type_soutenance && (
                <p className="text-red-600 text-xs mt-1">{formErrors.type_soutenance}</p>
              )}
            </div>

            <div>
              <FormInput
                label="Titre du travail"
                name="titre_travail"
                value={formData.titre_travail || ''}
                onChange={onInputChange}
                error={formErrors.titre_travail}
                required
                placeholder="Titre du mémoire/thèse..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Encadrement */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center space-x-2">
          <User className="h-5 w-5" />
          <span>Encadrement</span>
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Directeur interne
            </label>
            <select
              name="directeur_interne"
              value={formData.directeur_interne || ''}
              onChange={(e) => {
                onInputChange(e);
                if (e.target.value) {
                  onInputChange({ target: { name: 'directeur_externe', value: '' } });
                }
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
            >
              <option value="">Sélectionner un enseignant</option>
              {enseignantsInternes.map(enseignant => (
                <option key={enseignant.id} value={enseignant.id}>
                  {enseignant.prenom} {enseignant.nom}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Directeur externe
            </label>
            <select
              name="directeur_externe"
              value={formData.directeur_externe || ''}
              onChange={(e) => {
                onInputChange(e);
                if (e.target.value) {
                  onInputChange({ target: { name: 'directeur_interne', value: '' } });
                }
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
            >
              <option value="">Sélectionner un enseignant externe</option>
              {enseignantsExternes.map(enseignant => (
                <option key={enseignant.id} value={enseignant.id}>
                  {enseignant.nom_complet}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Co-directeur interne
            </label>
            <select
              name="co_directeur_interne"
              value={formData.co_directeur_interne || ''}
              onChange={(e) => {
                onInputChange(e);
                if (e.target.value) {
                  onInputChange({ target: { name: 'co_directeur_externe', value: '' } });
                }
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
            >
              <option value="">Sélectionner un co-directeur</option>
              {enseignantsInternes.map(enseignant => (
                <option key={enseignant.id} value={enseignant.id}>
                  {enseignant.prenom} {enseignant.nom}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Co-directeur externe
            </label>
            <select
              name="co_directeur_externe"
              value={formData.co_directeur_externe || ''}
              onChange={(e) => {
                onInputChange(e);
                if (e.target.value) {
                  onInputChange({ target: { name: 'co_directeur_interne', value: '' } });
                }
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
            >
              <option value="">Sélectionner un co-directeur externe</option>
              {enseignantsExternes.map(enseignant => (
                <option key={enseignant.id} value={enseignant.id}>
                  {enseignant.nom_complet}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="text-xs text-gray-600 mt-2">
          * Sélectionnez soit un directeur interne, soit un directeur externe
        </p>
      </div>

      {/* Planification */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center space-x-2">
          <Calendar className="h-5 w-5" />
          <span>Planification</span>
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Jury *
            </label>
            <select
              name="jury"
              value={formData.jury || ''}
              onChange={onInputChange}
              required
              className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${
                formErrors.jury ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Sélectionner un jury</option>
              {jurys.map(jury => (
                <option key={jury.id} value={jury.id}>
                  {jury.nom}
                </option>
              ))}
            </select>
            {formErrors.jury && (
              <p className="text-red-600 text-xs mt-1">{formErrors.jury}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Salle *
            </label>
            <select
              name="salle"
              value={formData.salle || ''}
              onChange={onInputChange}
              required
              className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${
                formErrors.salle ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Sélectionner une salle</option>
              {salles.map(salle => (
                <option key={salle.id} value={salle.id}>
                  {salle.libelle} (Capacité: {salle.capacite})
                </option>
              ))}
            </select>
            {formErrors.salle && (
              <p className="text-red-600 text-xs mt-1">{formErrors.salle}</p>
            )}
          </div>

          <div>
            <FormInput
              label="Date et heure"
              name="date_soutenance"
              type="datetime-local"
              value={formData.date_soutenance || ''}
              onChange={onInputChange}
              error={formErrors.date_soutenance}
              required
            />
          </div>

          <div>
            <FormInput
              label="Durée prévue (minutes)"
              name="duree_prevue"
              type="number"
              value={formData.duree_prevue || ''}
              onChange={onInputChange}
              error={formErrors.duree_prevue}
              required
              min="30"
              max="480"
              placeholder="120"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Observations
            </label>
            <textarea
              name="observations"
              value={formData.observations || ''}
              onChange={onInputChange}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
              placeholder="Observations particulières..."
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default SoutenanceForm; 